/** @jsx jsx */
import { ClassNames, css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ValuePropItem } from '@coursera-web/cds-labs';
import { MODULE_LAYOUTS, ValuePropsModule } from '@coursera-web/cds-labs';

import Markdown from 'bundles/cms/components/Markdown';
import type { UniversityProgramDegreeDescriptionPageAdmissionsProcessLinks as AdmissionsProcessLinks } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import type { ProgramDetailsPanelFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import AdmissionRequirementsModule from 'bundles/degree-description/components/modules/program-details/AdmissionRequirementsModule';
import CurriculumModule from 'bundles/degree-description/components/modules/program-details/CurriculumModule';
import PayingForYourDegreeModule from 'bundles/degree-description/components/modules/program-details/PayingForYourDegreeModule';
import type { ProgramDetailsTab } from 'bundles/degree-description/components/modules/program-details/ProgramDetailsSection';
import ProgramDetailsSection from 'bundles/degree-description/components/modules/program-details/ProgramDetailsSection';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import getAdmissionRequirementsSections from 'bundles/degree-description/utils/getAdmissionRequirementsSections';
import getProgramDetailsValueProps from 'bundles/degree-description/utils/getProgramDetailsValueProps';

import _t from 'i18n!nls/degree-description';

type Props = {
  pageContent: PropsFromDegreePageContent['pageContent'];
};

type ValuePropsTabContentProps = {
  heading?: string | null;
  description?: string | null;
  valueProps: ValuePropItem[];
};

const styles = {
  valuePropsTabCardClassName: css`
    padding: 0 !important;
    margin-top: var(--cds-spacing-200);
  `,
  zeroPaddingLayout: css`
    && {
      padding: 0;
    }
  `,
};

const ValuePropsTabContent = ({ heading, description, valueProps }: ValuePropsTabContentProps) => (
  <ClassNames>
    {({ css: cx }) => (
      <ValuePropsModule
        headline={heading}
        description={description ? <Markdown source={description} /> : null}
        valueProps={valueProps}
        layout={MODULE_LAYOUTS.textTopImageBottom}
        cardBackgroundColor="transparent"
        cardClassName={cx(styles.valuePropsTabCardClassName)}
        customPageContainerStyles={[styles.zeroPaddingLayout]}
      />
    )}
  </ClassNames>
);

/** Uses the primary value props, falling back to the secondary ones when there are none. */
const getPanelValueProps = (panel: ProgramDetailsPanelFragment, idPrefix: string) => {
  const primary = getProgramDetailsValueProps(panel.primaryValuePropsCollection, idPrefix);

  return primary.length > 0
    ? primary
    : getProgramDetailsValueProps(panel.secondaryValuePropsCollection, `${idPrefix}-secondary`);
};

const ProgramDetails = ({ pageContent }: Props) => {
  const { academicBlocksCollection, admissionsProcess, redesign2026Modules } = pageContent;
  const { programDetails } = redesign2026Modules ?? {};
  const { whatYoullLearn, formatSchedule, payingForYourDegree } = programDetails ?? {};

  const admissionRequirementsSections = getAdmissionRequirementsSections(
    admissionsProcess?.json,
    admissionsProcess?.links as AdmissionsProcessLinks | undefined
  );
  const curriculumItems = (academicBlocksCollection?.items ?? []).flatMap((item) =>
    item?.question
      ? [{ id: item.sys.id, title: item.question, supportText: item.subtitle ?? '', answer: item.answer ?? '' }]
      : []
  );

  const tabs: ProgramDetailsTab[] = (() => {
    const tabArray: ProgramDetailsTab[] = [];

    if (whatYoullLearn?.heading) {
      tabArray.push({
        tabTitle: whatYoullLearn.heading,
        tabContents: (
          <ValuePropsTabContent
            heading={whatYoullLearn.heading}
            description={whatYoullLearn.description}
            valueProps={getPanelValueProps(whatYoullLearn, 'what-youll-learn')}
          />
        ),
      });
    }

    tabArray.push(
      {
        tabTitle: _t('Admission requirements'),
        tabContents: (
          <AdmissionRequirementsModule
            heading={_t('Admission requirements')}
            description={_t('Here are the basic details on getting into this degree program.')}
            sections={admissionRequirementsSections}
          />
        ),
      },
      {
        tabTitle: _t('Curriculum'),
        tabContents: <CurriculumModule heading={_t('Curriculum')} items={curriculumItems} />,
      }
    );

    if (formatSchedule?.heading) {
      tabArray.push({
        tabTitle: formatSchedule.heading,
        tabContents: (
          <ValuePropsTabContent
            heading={formatSchedule.heading}
            description={formatSchedule.description}
            valueProps={getPanelValueProps(formatSchedule, 'format-schedule')}
          />
        ),
      });
    }

    if (payingForYourDegree?.heading) {
      tabArray.push({
        tabTitle: payingForYourDegree.heading,
        tabContents: (
          <PayingForYourDegreeModule
            heading={payingForYourDegree.heading}
            description={payingForYourDegree.description}
            stats={getProgramDetailsValueProps(payingForYourDegree.primaryValuePropsCollection, 'paying-stat')}
            items={getProgramDetailsValueProps(payingForYourDegree.secondaryValuePropsCollection, 'paying-item')}
          />
        ),
      });
    }

    return tabArray;
  })();

  return (
    <ProgramDetailsSection
      sectionHeading={programDetails?.sectionHeading || _t('See the program details')}
      tabs={tabs}
    />
  );
};

export default ProgramDetails;
